import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClubApi } from "../../services/clubApi";

type CaseOption = {
  caseIndex: number;
  title: string;
  briefingTitle?: string;
};

const isNewFormatCase = (clubCase: any) => {
  if (clubCase?.isNewFormatCase === true) return true;
  const caseIndex = Number(clubCase?.caseIndex || 0);
  if (!Number.isFinite(caseIndex) || caseIndex < 1000) return false;
  const approvalStatus = String(clubCase?.approvalStatus || "").trim().toLowerCase();
  if (["approved", "published_ready", "published"].includes(approvalStatus)) return true;
  if (String(clubCase?.generatedCaseId || "").trim()) return true;
  return (
    Array.isArray(clubCase?.casePayload?.evidence_files) && clubCase.casePayload.evidence_files.length > 0
  );
};

const pad = (value: number) => String(value).padStart(2, "0");

const buildDefaults = () => {
  const now = new Date();
  const sessionDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const sessionStartTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return { sessionDate, sessionStartTime, durationHours: 6 as 3 | 6 | 12 };
};

const buildQrImageUrl = (value: string) => {
  const encoded = encodeURIComponent(value);
  return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encoded}`;
};

const SESSION_SHARE_STORAGE_KEY = "club_group_session_share_v1";

type StoredSessionShare = {
  sessionId: string;
  guestUrl: string;
  inviteText: string;
  updatedAt: string;
};

const readStoredSessionShares = (): Record<string, StoredSessionShare> => {
  try {
    const raw = window.localStorage.getItem(SESSION_SHARE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, StoredSessionShare> | null;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const persistSessionShare = (share: StoredSessionShare) => {
  try {
    const existing = readStoredSessionShares();
    existing[share.sessionId] = share;
    window.localStorage.setItem(SESSION_SHARE_STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // Ignore storage failures (private mode / blocked storage).
  }
};

const ClubSessionSetupPage = () => {
  const [searchParams] = useSearchParams();
  const queryCaseIndex = Number(searchParams.get("caseIndex") || 0);
  const queryCaseName = String(searchParams.get("caseName") || "").trim();
  const isCaseContext = Number.isInteger(queryCaseIndex) && queryCaseIndex > 0;

  const defaults = useMemo(() => buildDefaults(), []);
  const [loadingCases, setLoadingCases] = useState(!isCaseContext);
  const [cases, setCases] = useState<CaseOption[]>([]);
  const [selectedCaseIndex, setSelectedCaseIndex] = useState<number>(isCaseContext ? queryCaseIndex : 0);
  const [sessionDate, setSessionDate] = useState(defaults.sessionDate);
  const [sessionStartTime, setSessionStartTime] = useState(defaults.sessionStartTime);
  const [durationHours, setDurationHours] = useState<3 | 6 | 12>(defaults.durationHours);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [sessionResult, setSessionResult] = useState<any>(null);
  const [ownerSession, setOwnerSession] = useState<any>(null);

  useEffect(() => {
    if (isCaseContext) return;

    const loadCases = async () => {
      try {
        setLoadingCases(true);
        const response = await ClubApi.getEntitlements();
        const caseOptions = (response?.cases || [])
          .filter((clubCase: any) => isNewFormatCase(clubCase))
          .map((clubCase: any) => ({
            caseIndex: Number(clubCase.caseIndex || 0),
            title: String(clubCase.title || "").trim(),
            briefingTitle: String(clubCase.briefingTitle || "").trim(),
          }))
          .filter((clubCase: CaseOption) => Number.isInteger(clubCase.caseIndex) && clubCase.caseIndex >= 1000)
          .sort((a: CaseOption, b: CaseOption) => b.caseIndex - a.caseIndex);

        setCases(caseOptions);
        if (!selectedCaseIndex && caseOptions.length > 0) {
          setSelectedCaseIndex(caseOptions[0].caseIndex);
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || "Failed to load your entitled cases.");
      } finally {
        setLoadingCases(false);
      }
    };

    loadCases();
  }, [isCaseContext, selectedCaseIndex]);

  useEffect(() => {
    if (!sessionResult?.session?.id) return;

    let timer: ReturnType<typeof setInterval> | null = null;
    let isMounted = true;

    const poll = async () => {
      try {
        const response = await ClubApi.getGroupSessionOwnerStatus(sessionResult.session.id);
        if (!isMounted) return;
        setOwnerSession(response?.session || null);
      } catch {
        // Keep existing display; owner can refresh page if needed.
      }
    };

    poll();
    timer = setInterval(poll, 10000);

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [sessionResult?.session?.id]);

  const selectedCaseName = useMemo(() => {
    if (isCaseContext) return queryCaseName || `Case #${selectedCaseIndex}`;
    const found = cases.find((clubCase) => clubCase.caseIndex === selectedCaseIndex);
    return found?.briefingTitle || found?.title || `Case #${selectedCaseIndex}`;
  }, [cases, isCaseContext, queryCaseName, selectedCaseIndex]);

  const handleCreate = async () => {
    if (!Number.isInteger(selectedCaseIndex) || selectedCaseIndex < 1000) {
      setError("Select a playable case first.");
      return;
    }

    try {
      setCreating(true);
      setError("");
      setCopyState("idle");

      const response = await ClubApi.createGroupSession({
        caseIndex: selectedCaseIndex,
        sessionDate,
        sessionStartTime,
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
        durationHours,
      });

      setSessionResult(response);
      setOwnerSession(null);
      const sessionId = String(response?.session?.id || "").trim();
      const guestUrl = String(response?.guestUrl || "").trim();
      if (sessionId && guestUrl) {
        persistSessionShare({
          sessionId,
          guestUrl,
          inviteText: String(response?.inviteText || "").trim(),
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to create session.");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async () => {
    const url = String(sessionResult?.guestUrl || "").trim();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const handleCopyInvite = async () => {
    const inviteText = String(sessionResult?.inviteText || "").trim();
    const url = String(sessionResult?.guestUrl || "").trim();
    if (!inviteText || !url) return;

    try {
      await navigator.clipboard.writeText(`${inviteText}\n${url}`);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const handleShare = async () => {
    const url = String(sessionResult?.guestUrl || "").trim();
    const inviteText = String(sessionResult?.inviteText || "").trim();
    if (!url) return;

    if (!navigator.share) {
      handleCopyInvite();
      return;
    }

    try {
      await navigator.share({
        title: "Cold Case Group Session",
        text: inviteText,
        url,
      });
    } catch {
      // User cancelled native share.
    }
  };

  const handleCopyQr = async () => {
    const url = String(sessionResult?.guestUrl || "").trim();
    if (!url) return;
    const qrUrl = buildQrImageUrl(url);

    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard?.write && typeof ClipboardItemCtor !== "undefined") {
        const response = await fetch(qrUrl);
        const qrBlob = await response.blob();
        const item = new ClipboardItemCtor({
          [qrBlob.type || "image/png"]: qrBlob,
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(qrUrl);
      }
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const handleCloseSession = async () => {
    const sessionId = String(sessionResult?.session?.id || "").trim();
    if (!sessionId) return;

    try {
      const response = await ClubApi.closeGroupSession(sessionId);
      setOwnerSession((previous: any) => ({
        ...(previous || {}),
        status: String(response?.session?.status || "closed"),
      }));
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to close session.");
    }
  };

  return (
    <section className="mx-auto max-w-4xl px-4 py-6 text-white">
      <div className="rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-[#0a1222] via-[#121726] to-[#231417] p-6 shadow-[0_30px_90px_rgba(8,18,40,0.5)]">
        <p className="text-xs uppercase tracking-[0.28em] text-cyan-200">Group Session</p>
        <h1 className="mt-2 text-3xl font-black">Start Group Session</h1>
        <p className="mt-3 text-sm text-zinc-200">
          Create a temporary guest link scoped to one case. Guests can join with a display name and play only this case while the session is valid.
        </p>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-semibold text-zinc-200">Case</span>
            {isCaseContext ? (
              <div className="rounded-lg border border-white/15 bg-white/5 px-3 py-2">{selectedCaseName}</div>
            ) : (
              <select
                value={selectedCaseIndex || ""}
                onChange={(event) => setSelectedCaseIndex(Number(event.target.value || 0))}
                className="w-full rounded-lg border border-white/20 bg-zinc-900 px-3 py-2"
                disabled={loadingCases}
              >
                <option value="">Select case</option>
                {cases.map((clubCase) => (
                  <option key={clubCase.caseIndex} value={clubCase.caseIndex}>
                    #{clubCase.caseIndex} - {clubCase.briefingTitle || clubCase.title}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-semibold text-zinc-200">Day</span>
            <input
              type="date"
              value={sessionDate}
              onChange={(event) => setSessionDate(event.target.value)}
              className="w-full rounded-lg border border-white/20 bg-zinc-900 px-3 py-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-semibold text-zinc-200">Start time</span>
            <input
              type="time"
              value={sessionStartTime}
              onChange={(event) => setSessionStartTime(event.target.value)}
              className="w-full rounded-lg border border-white/20 bg-zinc-900 px-3 py-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-semibold text-zinc-200">Duration</span>
            <select
              value={durationHours}
              onChange={(event) => setDurationHours(Number(event.target.value) as 3 | 6 | 12)}
              className="w-full rounded-lg border border-white/20 bg-zinc-900 px-3 py-2"
            >
              <option value={3}>3 hours</option>
              <option value={6}>6 hours</option>
              <option value={12}>12 hours</option>
            </select>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || loadingCases}
            className="rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#0a1222] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create Session"}
          </button>
          <Link to="/agency/dashboard" className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold">
            Back to Dashboard
          </Link>
        </div>

        {error && <p className="mt-4 text-sm text-rose-300">{error}</p>}
      </div>

      {sessionResult?.guestUrl ? (
        <div className="mt-6 grid gap-5 rounded-2xl border border-white/15 bg-zinc-950/80 p-5 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <h2 className="text-xl font-bold">Share Session</h2>
            <p className="mt-2 text-sm text-zinc-300">{sessionResult.inviteText}</p>
            <div className="mt-3 rounded-lg border border-white/15 bg-black/30 p-3 text-xs break-all">
              {sessionResult.guestUrl}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={handleCopyLink} className="rounded-md bg-zinc-800 px-3 py-2 text-xs font-semibold hover:bg-zinc-700">
                Copy Link
              </button>
              <button type="button" onClick={handleCopyInvite} className="rounded-md bg-zinc-800 px-3 py-2 text-xs font-semibold hover:bg-zinc-700">
                Copy Invite Text
              </button>
              <button type="button" onClick={handleShare} className="rounded-md bg-zinc-800 px-3 py-2 text-xs font-semibold hover:bg-zinc-700">
                Share
              </button>
              <button type="button" onClick={handleCopyQr} className="rounded-md bg-zinc-800 px-3 py-2 text-xs font-semibold hover:bg-zinc-700">
                Copy QR
              </button>
              <button type="button" onClick={handleCloseSession} className="rounded-md border border-rose-300/30 px-3 py-2 text-xs font-semibold text-rose-100 hover:bg-rose-500/10">
                Close Session
              </button>
            </div>
            {copyState === "copied" && <p className="mt-2 text-xs text-emerald-300">Copied.</p>}
            {copyState === "failed" && <p className="mt-2 text-xs text-rose-300">Copy failed.</p>}
            <div className="mt-4 text-xs text-zinc-300">
              Status: {ownerSession?.status || sessionResult?.session?.status || "active"}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-300">QR</p>
              <img
                src={buildQrImageUrl(sessionResult.guestUrl)}
                alt="Session QR code"
                className="mt-2 w-full max-w-[260px] rounded-lg bg-white p-2"
              />
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-300">Joined Players</p>
              <ul className="mt-2 space-y-1 text-sm text-zinc-100">
                {(ownerSession?.participants || []).length === 0 ? (
                  <li className="text-zinc-300">No one joined yet.</li>
                ) : (
                  (ownerSession?.participants || []).map((participant: any, index: number) => (
                    <li key={`${participant.displayName}-${index}`}>
                      {participant.displayName} <span className="text-zinc-400">({new Date(participant.joinedAt).toLocaleTimeString()})</span>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ClubSessionSetupPage;
